import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Target, Link2, X, ChevronDown } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { listGoals, updateProjectLinkedGoals } from '../lib/api';
import { queryKeys } from '../lib/queryKeys';
import type { LinkedKr, Goal } from '../types';

const KR_STATUS_COLORS: Record<string, string> = {
  not_started: 'bg-gray-200 dark:bg-white/10',
  in_progress: 'bg-blue-400',
  at_risk: 'bg-orange-400',
  achieved: 'bg-green-500',
};

function ProgressBar({ value }: { value: number }) {
  const color = value >= 100 ? 'bg-green-500' : value >= 60 ? 'bg-blue-500' : value >= 30 ? 'bg-amber-400' : 'bg-gray-200 dark:bg-white/10';
  return (
    <div className="h-1 w-full bg-gray-100 dark:bg-white/10 rounded-full overflow-hidden">
      <div className={`h-full rounded-full ${color}`} style={{ width: `${value}%` }} />
    </div>
  );
}

interface Props {
  projectId: string;
  linkedKrIds: LinkedKr[];
  canEdit: boolean;
}

export function ProjectGoals({ projectId, linkedKrIds, canEdit }: Props) {
  const qc = useQueryClient();
  const { data: goals = [] } = useQuery({ queryKey: queryKeys.goals, queryFn: listGoals });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [expandedGoals, setExpandedGoals] = useState<Set<string>>(new Set());

  const mutation = useMutation({
    mutationFn: (ids: LinkedKr[]) => updateProjectLinkedGoals(projectId, ids),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.project(projectId) }),
  });

  // Resolve linked KRs to their full objects
  const linkedItems = linkedKrIds.flatMap((link) => {
    const goal = goals.find((g) => g.goalId === link.goalId);
    if (!goal) return [];
    const kr = goal.keyResults.find((k) => k.krId === link.krId);
    if (!kr) return [];
    return [{ goal, kr, link }];
  });

  // Group by goal for display
  const byGoal = new Map<string, { goal: Goal; krs: typeof linkedItems }>();
  for (const item of linkedItems) {
    const existing = byGoal.get(item.goal.goalId);
    if (existing) existing.krs.push(item);
    else byGoal.set(item.goal.goalId, { goal: item.goal, krs: [item] });
  }

  function toggleKr(goalId: string, krId: string) {
    const exists = linkedKrIds.some((l) => l.goalId === goalId && l.krId === krId);
    const next = exists
      ? linkedKrIds.filter((l) => !(l.goalId === goalId && l.krId === krId))
      : [...linkedKrIds, { goalId, krId }];
    mutation.mutate(next);
  }

  const isLinked = (goalId: string, krId: string) =>
    linkedKrIds.some((l) => l.goalId === goalId && l.krId === krId);

  if (goals.length === 0) return null;

  return (
    <div className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-meta font-semibold text-gray-500 dark:text-gray-400 flex items-center gap-1.5 uppercase tracking-wider">
          <Target className="h-4 w-4 text-primary" />
          Goals & OKRs
        </h3>
        {canEdit && (
          <button
            onClick={() => setPickerOpen((p) => !p)}
            className="flex items-center gap-1.5 text-meta px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 hover:border-primary/40 hover:text-primary transition-colors focus-visible:ring-2 focus-visible:ring-primary/50"
          >
            <Link2 className="h-4 w-4" />
            Link KRs
          </button>
        )}
      </div>

      {/* Linked KRs display */}
      {byGoal.size === 0 ? (
        <p className="text-meta text-gray-400 dark:text-gray-500 italic">
          {canEdit ? 'No key results linked. Click "Link KRs" to connect this project to your OKRs.' : 'No key results linked.'}
        </p>
      ) : (
        <div className="space-y-4">
          {Array.from(byGoal.values()).map(({ goal, krs }) => (
            <div key={goal.goalId}>
              <p className="text-body font-medium text-gray-700 dark:text-gray-300 mb-1.5 truncate">
                {goal.title}
                {goal.timeframe && <span className="ml-1.5 text-gray-400">· {goal.timeframe}</span>}
              </p>
              <div className="space-y-1.5 pl-3 border-l-2 border-gray-100 dark:border-white/[0.07]">
                {krs.map(({ kr }) => (
                  <div key={kr.krId} className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-body text-gray-800 dark:text-gray-200 truncate">{kr.title}</p>
                      {kr.metric && <p className="text-meta text-gray-400 mt-0.5">{kr.metric}</p>}
                      <div className="flex items-center gap-2 mt-1">
                        <ProgressBar value={kr.progress} />
                        <span className="text-meta text-gray-400 flex-shrink-0">{kr.progress}%</span>
                      </div>
                    </div>
                    <span className={`h-2 w-2 rounded-full flex-shrink-0 ${KR_STATUS_COLORS[kr.status] ?? 'bg-gray-200'}`} />
                    {canEdit && (
                      <button onClick={() => toggleKr(goal.goalId, kr.krId)}
                        className="text-gray-300 dark:text-gray-600 hover:text-red-400 transition-colors flex-shrink-0">
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Picker dropdown */}
      <AnimatePresence>
        {pickerOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden mt-4 border-t border-gray-100 dark:border-white/[0.06] pt-4"
          >
            <p className="text-meta font-medium text-gray-500 dark:text-gray-400 mb-3">Select key results to link:</p>
            <div className="space-y-4 max-h-64 overflow-y-auto">
              {goals.filter((g) => g.status === 'active').map((goal) => (
                <div key={goal.goalId}>
                  <button
                    onClick={() => setExpandedGoals((p) => {
                      const n = new Set(p);
                      n.has(goal.goalId) ? n.delete(goal.goalId) : n.add(goal.goalId);
                      return n;
                    })}
                    className="w-full flex items-center gap-2 text-left py-1"
                  >
                    <ChevronDown className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${expandedGoals.has(goal.goalId) ? 'rotate-180' : ''}`} />
                    <span className="text-meta font-medium text-gray-700 dark:text-gray-300 truncate">{goal.title}</span>
                    {goal.timeframe && <span className="text-meta text-gray-400 flex-shrink-0">{goal.timeframe}</span>}
                  </button>
                  {expandedGoals.has(goal.goalId) && (
                    <div className="pl-5 space-y-1 mt-1">
                      {goal.keyResults.map((kr) => {
                        const linked = isLinked(goal.goalId, kr.krId);
                        return (
                          <button
                            key={kr.krId}
                            onClick={() => toggleKr(goal.goalId, kr.krId)}
                            className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-left transition-colors ${
                              linked
                                ? 'bg-primary/10 dark:bg-primary/20 text-primary'
                                : 'hover:bg-gray-50 dark:hover:bg-white/[0.04] text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            <span className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${KR_STATUS_COLORS[kr.status] ?? 'bg-gray-200'}`} />
                            <span className="text-meta truncate">{kr.title}</span>
                            {kr.metric && <span className="text-meta text-gray-400 ml-auto flex-shrink-0 truncate max-w-[120px]">{kr.metric}</span>}
                          </button>
                        );
                      })}
                      {goal.keyResults.length === 0 && (
                        <p className="text-micro text-gray-400 pl-2 italic">No key results defined.</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
